/**
 * When a file may take the meta-agent's side panel — the ONE rule, used by
 * InspectorContext.setActiveArtifact. Pure, so tests/unit/agent_of_agents/
 * artifact_panel_switch.test.mjs runs it as written.
 */
import type { InspectorItem } from './components/InspectorContext';

/** Who asked the panel to show a file.
 *  `user`: a click. `stream`: the meta-agent wrote, edited or showed it in the
 *  turn now running (artifact_start / artifact_render / artifact_show).
 *  `restore`: a reopened conversation's newest file. */
export type ArtifactActivationSource = 'user' | 'stream' | 'restore';

export function fileMayTakeScreen(source: ArtifactActivationSource, current: InspectorItem | null): boolean {
  // A user who CLICKS a file chip means it.
  if (source === 'user') return true;
  // Nothing automatic takes the screen away from a card that is waiting on an
  // answer: the file is only a preview, while the card is the question, and a
  // card the user cannot see is one the backend auto-rejects as "user moved
  // on" a few seconds later. The Files tab's count still says the file is there.
  if (current?.kind === 'proposal' && current.proposal.status === 'pending') return false;
  // A reopened conversation's newest file never displaces a simulation the
  // user is watching (2026-09-19). A file the meta-agent produced in THIS turn
  // does: it says the file is in the side panel, and a simulation it ran
  // earlier in the turn is over. Yielding to it kept the test chat on screen,
  // so the new file appeared only after leaving and coming back (item 59;
  // production, m2-team, 2026-10-28: every Word turn had tested an agent first).
  if (source === 'restore' && current?.kind === 'simulation') return false;
  return true;
}

/** The simulation view draws its own header, so the panel's title row steps
 *  aside — but only while that view is on screen. Over the Files tab (a file
 *  produced after a simulation) the panel needs its title row back. */
export function simulationOnScreen(active: InspectorItem | null, panelTab: 'cards' | 'files'): boolean {
  return active?.kind === 'simulation' && panelTab === 'cards';
}
